import { AddCircleOutline, CalendarMonth, List } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { TabbedContainer, TabConfigurationProps } from 'src/components/tabs/GenericTabbedContainer'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { useCustomerCtx, useDialogCtx } from '../../../hooks/context-hooks'
import RecurringTasksTab from './tasks/RecurringTasksTab'
import TaskForm from './tasks/TaskForm'
import WorkOrderOccurrencesTab from './tasks/WorkOrderOccurrencesTab'

const Tasks = () => {
  const { t } = useTranslation('task')
  const theme = useTheme()
  const { customer } = useCustomerCtx()
  const { showDialog } = useDialogCtx()
  const [activeTab, setActiveTab] = useState(0)
  const [isCalendar, setIsCalendar] = useState(false)

  const tabConfigs: TabConfigurationProps[] = useMemo(
    () => [
      {
        id: 0,
        title: t('work-orders', { ns: 'task' }),
        componentProps: { customerId: customer?.id, isCalendar },
        ContentComponent: WorkOrderOccurrencesTab,
      },
      {
        id: 1,
        title: t('recurring', { ns: 'task' }),
        componentProps: { customerId: customer?.id },
        ContentComponent: RecurringTasksTab,
      },
    ],
    [customer?.id, isCalendar, t],
  )

  const onAdd = useCallback(() => {
    showDialog(
      TaskForm,
      {
        componentProps: { customerId: customer?.id },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }, [customer?.id, showDialog])

  const headerActions = [
    ...(activeTab === 0
      ? [
        <GridActionButton
          key="calendar-toggle"
          buttonIcon={isCalendar ? <List /> : <CalendarMonth />}
          onClick={() => setIsCalendar(v => !v)}
          variant={'outlined'}
          height={'44px'}
          shadowWeight={null}
          border={1}
          toolTipTitle={isCalendar ? t('list-view', { ns: 'task' }) : t('task-calendar', { ns: 'task' })}
        />,
      ]
      : []),
    <GridActionButton
      key="add-work-order"
      buttonText={t('add-task')}
      buttonIcon={<AddCircleOutline sx={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} />}
      onClick={onAdd}
      variant={'contained'}
      height={'44px'}
      shadowWeight={null}
      border={0}
      toolTipTitle={t('add-task')}
    />,
  ]

  return (
    <CommonPageLayout titleSection={{ title: t('task-management') }}>
      <Box bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'} padding={'8px'}>
        <TabbedContainer
          tabConfigs={tabConfigs}
          currentActiveTab={activeTab}
          onTabChange={setActiveTab}
          headerActions={headerActions}
          isLargeTabHeader
        />
      </Box>
    </CommonPageLayout>
  )
}

export default Tasks
